<template>
  <div
    class="flex items-center justify-center gap-1.5 rounded-full"
    :class="dynamicClasses"
  >
    <div class="whitespace-nowrap">
      {{ text }}
    </div>
    <div
      v-if="iconTooltipText !== ''"
      :class="{ '-ml-1': icon !== '' }"
    >
      <Popper
        placement="top"
        :hover="true"
        trigger="hover"
        :arrow="true"
        offset-distance="6"
      >
        <div
          v-if="icon !== ''"
          class="relative inline-block"
          :class="iconClasses"
        >
          <svg
            v-if="icon == 'arrow-up'"
            class="h-full w-full"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 5.39062L5 1.39063L9 5.39062"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M5 1.89062L5 9.39062"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
          <svg
            v-if="icon === 'arrow-down'"
            class="h-full w-full"
            viewBox="0 0 11 14"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.5 11.9998L4.79289 12.7069L5.5 13.414L6.20711 12.7069L5.5 11.9998ZM0.792894 8.70692L4.79289 12.7069L6.20711 11.2927L2.20711 7.29271L0.792894 8.70692ZM6.20711 12.7069L10.2071 8.70692L8.79289 7.29271L4.79289 11.2927L6.20711 12.7069Z"
              fill="#DB3713"
            />
            <path
              d="M5.5 12L5.5 -5.96046e-08"
              stroke="#DB3713"
              stroke-width="2"
            />
          </svg>
          <div
            v-if="icon === 'loading'"
            class="loading-gradient h-full w-full rounded-full"
          />
          <svg
            v-if="icon == 'exclamation'"
            class="h-full w-full"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <svg
            v-if="icon == 'checkmark'"
            class="h-full w-full"
            fill="currentColor"
            viewBox="0 0 13 13"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z"
            />
          </svg>
          <svg
            v-if="icon == 'question'"
            class="h-full w-full"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
              clip-rule="evenodd"
            />
          </svg>
          <svg
            v-if="icon === 'dash'"
            class="h-full w-full"
            fill="currentColor"
            viewBox="0 0 6 15"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_1558_19752)">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M-3.00555 6.53246L8.99462 6.46752L9.00544 8.46749L-2.99473 8.53244L-3.00555 6.53246Z"
                fill="#4387D8"
              />
            </g>
            <defs>
              <clipPath id="clip0_1558_19752">
                <rect
                  width="6"
                  height="15"
                  fill="white"
                />
              </clipPath>
            </defs>
          </svg>
        </div>
        <template
          v-if="iconTooltipText !== ''"
          #content
        >
          <p
            class="icon-tooltip rounded-md bg-dark-gray px-2 py-1 text-center text-2xs text-light-gray"
          >
            {{ iconTooltipText }}
          </p>
        </template>
      </Popper>
    </div>
    <div
      v-else-if="icon !== ''"
      :class="{ 'relative -top-px flex': icon !== '' }"
    >
      <div
        v-if="icon !== ''"
        class="relative inline-block"
        :class="iconClasses"
      >
        <svg
          v-if="icon == 'arrow-up'"
          class="h-full w-full"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 5.39062L5 1.39063L9 5.39062"
            stroke="currentColor"
            stroke-width="1.5"
          />
          <path
            d="M5 1.89062L5 9.39062"
            stroke="currentColor"
            stroke-width="1.5"
          />
        </svg>
        <svg
          v-if="icon === 'arrow-down'"
          class="h-full w-full"
          viewBox="0 0 11 14"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.5 11.9998L4.79289 12.7069L5.5 13.414L6.20711 12.7069L5.5 11.9998ZM0.792894 8.70692L4.79289 12.7069L6.20711 11.2927L2.20711 7.29271L0.792894 8.70692ZM6.20711 12.7069L10.2071 8.70692L8.79289 7.29271L4.79289 11.2927L6.20711 12.7069Z"
            fill="#DB3713"
          />
          <path
            d="M5.5 12L5.5 -5.96046e-08"
            stroke="#DB3713"
            stroke-width="2"
          />
        </svg>
        <div
          v-if="icon === 'loading'"
          class="loading-gradient h-full w-full rounded-full"
        />
        <svg
          v-if="icon == 'exclamation'"
          class="h-full w-full"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-if="icon == 'checkmark'"
          class="h-full w-full"
          fill="currentColor"
          viewBox="0 0 13 13"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.30466 12.2119C9.35139 12.2119 11.8212 9.74201 11.8212 6.69529C11.8212 5.8144 11.6148 4.98173 11.2476 4.24303L6.49714 8.99348L5.93988 9.55074L5.38262 8.99348L2.95534 6.56621L4.06986 5.45169L5.93988 7.32171L10.3341 2.92748C9.32739 1.8513 7.89458 1.17871 6.30466 1.17871C3.25794 1.17871 0.788086 3.64857 0.788086 6.69529C0.788086 9.74201 3.25794 12.2119 6.30466 12.2119Z"
          />
        </svg>
        <svg
          v-if="icon == 'question'"
          class="h-full w-full"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
            clip-rule="evenodd"
          />
        </svg>
        <svg
          v-if="icon === 'dash'"
          class="h-full w-full"
          fill="currentColor"
          viewBox="0 0 6 15"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clip-path="url(#clip0_1558_19752)">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M-3.00555 6.53246L8.99462 6.46752L9.00544 8.46749L-2.99473 8.53244L-3.00555 6.53246Z"
              fill="#4387D8"
            />
          </g>
          <defs>
            <clipPath id="clip0_1558_19752">
              <rect
                width="6"
                height="15"
                fill="white"
              />
            </clipPath>
          </defs>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import Popper from 'vue3-popper';

export default {
  name: 'CustomBadge',
  components: {
    Popper,
  },
  props: {
    bgColorClass: {
      type: String,
      required: false,
    },
    colorClass: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: true,
    },
    text: {
      type: String,
      required: false,
    },
    icon: {
      type: String,
      required: false,
      default: '',
    },
    iconSize: {
      type: String,
      required: false,
      default: '',
    },
    iconColorClass: {
      type: String,
      required: false,
      default: '',
    },
    iconTooltipText: {
      type: String,
      required: false,
      default: '',
    },
  },
  computed: {
    sizeClasses() {
      switch (this.size) {
        case 'lg':
          return 'text-sm px-3 py-1';
        case 'md':
          return 'text-xs px-2 py-1';
        default:
          return '';
      }
    },
    iconSizeClasses() {
      if (this.iconSize == '') {
        switch (this.size) {
          case 'lg':
            return 'h-4 w-4';
          default:
            return 'h-4 w-4';
        }
      } else if (this.iconSize !== '') {
        switch (this.iconSize) {
          case '3xl':
            return 'h-7 w-7';
          case '2xl':
            return 'h-6 w-6';
          case 'xl':
            return 'h-5 w-5';
          case 'lg':
            return 'h-4 w-4';
          case 'md':
            return 'h-3 w-3';
          default:
            return 'h-3 w-3';
        }
      } else {
        return '';
      }
    },
    iconClasses() {
      return this.iconColorClass + ' ' + this.iconSizeClasses;
    },
    dynamicClasses() {
      return this.bgColorClass + ' ' + this.colorClass + ' ' + this.sizeClasses;
    },
  },
};
</script>

<style scoped>
:root {
  --popper-theme-background-color: #333333;
  --popper-theme-background-color-hover: #333333;
  --popper-theme-text-color: #ffffff;
  --popper-theme-border-width: 0px;
  --popper-theme-border-style: solid;
  --popper-theme-border-radius: 6px;
  --popper-theme-padding: 16px;
  --popper-theme-box-shadow: 0 6px 30px -6px rgba(0, 0, 0, 0.25);
}

.icon-tooltip {
  max-width: 120px;
}
</style>
